import { useEffect, useRef } from "react";
import { Quote } from "lucide-react";
import { throttle } from "@/lib/utils";

const testimonials = [
  {
    id: 1,
    content:
      "This wasn't just a trip—it was the most profound experience of my life. The guides understood that I wasn't there for tourism. They helped me find what I came to India searching for.",
    author: "Sarah Mitchell",
    location: "Portland, USA",
    journey: "Himalayan Meditation Retreat",
  },
  {
    id: 2,
    content:
      "After 20 years in corporate life, I needed to reconnect with something deeper. The Varanasi journey with Atman Yatra gave me the reset I desperately needed. I'm a different person now.",
    author: "Thomas Weber",
    location: "Munich, Germany",
    journey: "Sacred Ganges Pilgrimage",
  },
  {
    id: 3,
    content:
      "As someone who had practiced yoga for years in studios, experiencing it in Rishikesh with true masters was humbling and transformative. This is how yoga was meant to be taught.",
    author: "Emma Larsson",
    location: "Stockholm, Sweden",
    journey: "Yoga at the Source",
  },
];

export const Testimonials = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                el.classList.add("visible");
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const baseOffset = (scrollProgress - 0.5) * 25;

      // Direct DOM manipulation
      cardsRef.current.forEach((card, index) => {
        if (!card) return;
        const factor = index === 1 ? 1.2 : 0.8;
        const offset = baseOffset * factor;
        card.style.transform = `translateY(${offset}px)`;
      });
    }, 50);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32 bg-sand">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="reveal text-saffron text-sm tracking-[0.2em] uppercase font-medium">
            Traveler Stories
          </span>
          <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mt-4 mb-6">
            Journeys That <span className="text-maroon">Transform</span>
          </h2>
          <p className="reveal text-muted-foreground leading-relaxed">
            Hear from travelers who came seeking something more—and found it.
          </p>
        </div>

        {/* Testimonials Grid with Parallax */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="reveal"
              ref={(el) => (cardsRef.current[index] = el)}
              style={{ 
                transitionDelay: `${index * 150}ms`,
                // Initial transform handled by style, parallax via JS
                transition: "transform 0.1s linear"
              }}
            >
              <blockquote className="bg-background rounded-2xl p-8 h-full shadow-soft relative">
                {/* Quote Icon */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-saffron/20 to-gold/10 flex items-center justify-center mb-6">
                  <Quote className="w-5 h-5 text-saffron" />
                </div>

                {/* Content */}
                <p className="text-foreground leading-relaxed mb-6 italic">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <footer className="border-t border-border pt-4">
                  <p className="font-serif text-lg text-foreground">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {testimonial.location}
                  </p>
                  <p className="text-xs text-saffron mt-2">
                    {testimonial.journey}
                  </p>
                </footer>
              </blockquote>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
